// src/sections/Tools.tsx

import React, { useState } from 'react';
import clsx from 'clsx';
import {
  Cpu,
  Code,
  Server,
  Database,
  Lock,
  Globe,
} from 'lucide-react';

import PortfolioItem from '../components/PortfolioItem';

type Filter = 'all' | 'api-scraper' | 'infrastructure' | 'authentication' | 'website';

// Each tool should have a unique key—here we’ll use `title` if guaranteed unique.
// Alternatively, you can add an `id` field and use that as the key.
export interface ToolData {
  title: string;
  description: string;
  category: Filter;
  icon: React.ReactNode;
  link: string;
  size: 'small' | 'large';
  image?: string;
  video?: string;
}

export const toolsData: ToolData[] = [
    {
      title: 'Update Launcher',
      description:
        'A launcher updater for applications, ensuring users have the latest version.',
      category: 'infrastructure', // merged 'Server' + 'Launcher' => 'infrastructure'
      icon: <Database className="w-4 h-4" />,
      video: 'assets/tools/updatelauncher/updatelauncher.mp4',
      link: 'tools/update-launcher',
      size: 'large',
    },
    {
      title: 'GPT Auto Scraper',
      description: 'Automates scraping of websites using GPT-based intelligence.',
      category: 'api-scraper', // merged 'API' + 'Scraper' => 'api-scraper'
      icon: <Code className="w-4 h-4" />,
      image: 'assets/tools/gptautoscraper/GPT_AutoScrape.png',
      link: 'tools/gpt-auto-scraper',
      size: 'small',
    },
    {
      title: 'BingAI Unofficial API',
      description: 'Unofficial API for integrating BingAI capabilities into your applications.',
      category: 'api-scraper',
      icon: <Cpu className="w-4 h-4" />,
      image: 'assets/tools/bingai_unofficial_api/bing_unofficial_api.png',
      link: 'tools/bingai-unofficial-api',
      size: 'small',
    },
    {
      title: 'WixScraper',
      description: 'A robust scraper tailored for extracting data from Wix-based websites.',
      category: 'api-scraper',
      icon: <Code className="w-4 h-4" />,
      image: 'assets/tools/wix_scraper/wix_scraper.png',
      link: 'tools/wix-scraper',
      size: 'small',
    },
    {
      title: 'ChatGPT Unofficial API',
      description:
        'Originally built for the Davinci model during OpenAI Playground days, upgraded to GPT-3 and beyond.',
      category: 'api-scraper',
      icon: <Cpu className="w-4 h-4" />,
      image: 'assets/tools/gpt_unofficial_api/gpt_unofficial_api.png',
      link: 'tools/chatgpt-unofficial-api',
      size: 'small',
    },
    {
      title: 'Relay Server',
      description: 'A relay server implementation for server-client games.',
      category: 'infrastructure',
      icon: <Server className="w-4 h-4" />,
      image: 'assets/tools/relayserver/relay.png',
      link: 'tools/relay-server',
      size: 'small',
    },
    {
      title: 'DuckDuckGo Image Scraper',
      description: 'Scrapes images from DuckDuckGo search results efficiently.',
      category: 'api-scraper',
      icon: <Code className="w-4 h-4" />,
      image: 'assets/tools/duckduckgo_image_scraper/duckduckgo_imagescraper.png',
      link: 'tools/duckduckgo-image-scraper',
      size: 'small',
    },
    {
      title: 'Monstera Login Menu',
      description: 'A login menu built with Firebase for the Monstera application.',
      category: 'authentication',
      icon: <Lock className="w-4 h-4" />,
      image: 'assets/tools/monsteralogin/login.png',
      link: 'tools/monstera-login-menu',
      size: 'small',
    },
    {
      title: 'ACRC Website',
      description: 'ACRC official website with comprehensive resources and documentation.',
      category: 'website',
      icon: <Globe className="w-4 h-4" />,
      image: 'assets/tools/acrc_website/acrc_website.png',
      link: 'tools/acrc',
      size: 'small',
    },
    {
      title: 'Serverless Portfolio',
      description: 'A React portfolio that can be viewed, edited, and built client-side on your browser.',
      category: 'website',
      icon: <Globe className="w-4 h-4" />,
      image: 'assets/tools/serverlessportfolio/portfolio.png',
      link: 'tools/serverless-portfolio',
      size: 'small',
    },
];

const Tools: React.FC = () => {
  const [filter, setFilter] = useState<Filter>('all');

  // Filter logic
  const filteredTools = toolsData.filter((item) => {
    if (filter === 'all') return true;
    return item.category === filter;
  });

  // Fewer filters, all-lowercase
  const filterButtons = [
    { label: 'all', value: 'all', icon: <Database className="w-4 h-4" /> },
    { label: 'api-scraper', value: 'api-scraper', icon: <Code className="w-4 h-4" /> },
    { label: 'infrastructure', value: 'infrastructure', icon: <Server className="w-4 h-4" /> },
    { label: 'authentication', value: 'authentication', icon: <Lock className="w-4 h-4" /> },
    { label: 'website', value: 'website', icon: <Globe className="w-4 h-4" /> },
  ];

  return (
    <>
      <section id="tools" className="section">
        <div className="max-w-7xl mx-auto px-4 py-20">
          {/* Tools Label at Top Left */}
          <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-100 mb-8">
            Tools
          </h1>

          {/* Filter Buttons */}
          <div className="flex justify-center mb-8 space-x-4 flex-wrap">
            {filterButtons.map((btn) => (
              <button
                key={btn.value}
                onClick={() => setFilter(btn.value as Filter)}
                className={clsx(
                  'filter-btn px-4 py-2 rounded-full flex items-center space-x-2 transition-colors mb-2',
                  {
                    'bg-blue-600 text-white hover:bg-blue-700': filter === btn.value,
                    'bg-gray-200 dark:bg-gray-700 hover:bg-blue-600 hover:text-white':
                      filter !== btn.value,
                  }
                )}
              >
                {btn.icon}
                <span>{btn.label}</span>
              </button>
            ))}
          </div>

          {/* Tools Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8">
            {filteredTools.map((item) => (
              <PortfolioItem
                key={item.title} // using the title as a stable key
                data={item}
                className={clsx(
                  'col-span-1',
                  item.size === 'large' && 'sm:col-span-2'
                )}
              />
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default Tools;
